/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { ISpinnerProps, ISpinnerStyles, Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/Text';
import { ITooltipHostProps, ITooltipHostStyles } from '@fluentui/react/lib/Tooltip';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import differenceWith from 'lodash.differencewith';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { PillProps } from 'src/core/components/Pill';
import { PillContainer } from 'src/core/components/PillContainer';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { partial } from 'src/core/utils/function';
import { globalMessages } from 'src/core/utils/globalMessages';
import { Feature } from 'src/luis/modules/features/types/Feature';
import { LuisId } from 'src/luis/types/Resource';

const { styleComponent } = getStylistV2('ConversationFeaturePicker');
const scopedTheme = getScopedTheme('ConversationFeaturePicker');

const RequireIconButton = styleComponent<IButtonProps>(IconButton)(
    'RequireIconButton',
    (_, cssVars) => ({
        transition: 'opacity 100ms ease',
        opacity: cssVars.opacity,
        $nest: {
            '&:hover': {
                opacity: 1
            }
        }
    }),
    {
        cssVars: { opacity: '' },
        cssVarsSetter: ({ isRequired }: { isRequired: boolean }) => ({
            opacity: isRequired ? '1' : '0'
        })
    }
);

const toggleRequiredButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { width: 24, height: '100%' },
    rootHovered: { background: scopedTheme.toggleRequiredHoverBackground },
    rootPressed: { background: scopedTheme.toggleRequiredActiveBackground },
    icon: { fontSize: 12, color: scopedTheme.toggleIconColor }
});

const spinnerStyles = classNamesFunction<ISpinnerProps, ISpinnerStyles>()({
    root: { padding: 4 }
});

const tooltipStyles = classNamesFunction<ITooltipHostProps, ITooltipHostStyles>()({
    root: {
        height: '100%',
        selectors: {
            '&:hover button': {
                opacity: 1
            }
        }
    }
});

const addLabelStyles = classNamesFunction<ITextProps, ITextStyles>()({ root: { color: scopedTheme.addTextColor, marginLeft: 4 } });

/**
 * Gets the icon name for the pill or contextual
 * menu item based on the feature type.
 *
 * @param featureType The type to render the icon for.
 * @returns The icon name.
 */
const getIconName = (featureType: Feature['typeId']) => {
    switch (featureType) {
        case 'entity':
            return 'Accounts';
        case 'feature':
            return 'Dictionary';
        default:
            return 'FlowChart';
    }
};

// [sorgh]: This is the current limitation of the backend API for Build, to be revisited after.
// Bug 18087: Due to backend limits, only entities can be used as required features
const canBeRequired = (feature: Feature) => feature.typeId !== 'feature';

/**
 * Generates a contextual menu item based on the
 * given feature.
 *
 * @param feature The feature to get the item for.
 * @param onAddFeature A callback for when this item is clicked.
 * @returns A contextual menu item.
 */
const getContextualMenuItem = (feature: Feature, onAddFeature: () => void, disabledFeatureIds: LuisId[]): IContextualMenuItem => ({
    key: `${feature.id}`,
    itemType: ContextualMenuItemType.Normal,
    text: feature.name,
    iconProps: { iconName: getIconName(feature.typeId) },
    disabled: Boolean(disabledFeatureIds.find(id => id === feature.id)),
    style: disabledFeatureIds.find(id => id === feature.id) ? { textDecoration: 'line-through' } : {},
    onClick: onAddFeature
});

export type ConversationFeaturePickerProps = {
    /**
     * The list of features already added.
     */
    existingFeatures: Feature[];

    /**
     * The list of all features possible to add.
     */
    availableFeatures: Feature[];

    /**
     * Id of the required feature, if exists.
     */
    requiredFeatureId?: LuisId;

    /**
     * The ids of the disabled items of the available features dropdown.
     */
    disabledFeatureIds?: LuisId[];

    /**
     * List of feature ids that are being updated.
     */
    inProgressFeatureIds?: LuisId[];

    /**
     * The initial state of the feature picker input field.
     */
    showInputField?: boolean;

    /**
     * A callback for when a feature is added.
     */
    onAddFeature: (feature: Feature) => void;

    /**
     * A callback for when a feature is removed.
     */
    onRemoveFeature: (features: Feature) => void;

    /**
     * A callback for when new entity to be created.
     */
    onCreateNewPhraseList: (name: string) => void;

    /**
     * A callback for when an feature is clicked on.
     */
    onFeatureClick?: (feature: Feature) => void;

    /**
     * Callback to toggle required.
     */
    onToggleRequire?: (feature: Feature, required: boolean) => void;

    /**
     * User role to extract actions available for user.
     */
    userRole: string;

    /**
     * User role to show in luis tool tip
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    entityMenuLabel: {
        id: 'features.featurePicker.entityMenuLabel',
        defaultMessage: 'Entities'
    },
    phraseListMenuLabel: {
        id: 'features.featurePicker.phraseListMenuLabel',
        defaultMessage: 'Phrase lists'
    },
    createPhraseListItem: {
        id: 'features.featurePicker.createPhraseListItem',
        defaultMessage: 'Create new phrase list'
    },
    inputPlaceholder: {
        id: 'features.featurePicker.inputPlaceholder',
        defaultMessage: 'Type name ...'
    },
    addFeature: {
        id: 'features.featurePicker.addFeature',
        defaultMessage: 'Add feature'
    },
    requireTooltip: {
        id: 'features.featurePicker.requiredTooltip',
        defaultMessage: 'Require'
    },
    dontRequireTooltip: {
        id: 'features.featurePicker.dontRequiredTooltip',
        defaultMessage: "Don't Require"
    },
    disabledRequireTooltip: {
        id: 'features.featurePicker.disabledRequireTooltip',
        defaultMessage: 'Phrase lists cannot be used as required features at this time'
    }
});

/**
 * This component enables user to pick a feature
 * item (entity or phrase list) and render it
 * as a pill component
 */
export const ConversationFeaturePicker = (props: ConversationFeaturePickerProps) => {
    const {
        requiredFeatureId,
        existingFeatures,
        availableFeatures,
        onFeatureClick,
        onAddFeature,
        onRemoveFeature,
        onCreateNewPhraseList,
        disabledFeatureIds = [],
        inProgressFeatureIds = [],
        showInputField = false,
        onToggleRequire,
        userRole,
        displayedUserRole
    } = props;
    const { localize } = useLocalization();
    const [showInputPill, setShowInputPill] = React.useState(showInputField);

    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    /**
     * Construct pills from existing features.
     */
    const featurePills = React.useMemo(
        () =>
            existingFeatures.map<PillProps>((feature, index) => {
                const isRequired = requiredFeatureId === feature.id;
                const isDisabled = disabledFeatureIds.includes(feature.id);
                const isRequirable = canBeRequired(feature);
                const isInProgress = inProgressFeatureIds.includes(feature.id);
                const isRequiredToggleDisabled = isInProgress || !isRequirable || !canWrite;

                const label = localize(
                    messages[!isRequirable ? 'disabledRequireTooltip' : isRequired ? 'dontRequireTooltip' : 'requireTooltip']
                );

                return {
                    pillKey: feature.id,
                    name: feature.name,
                    iconProps: {
                        iconName: getIconName(feature.typeId)
                    },
                    index,
                    isDisabled,
                    onRemovePill: () => onRemoveFeature(feature),
                    onRenderPillContent: (pillProps?: PillProps, defaultRender?: (pillProps: PillProps) => React.ReactNode) => (
                        <Stack verticalFill horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                            <Stack data-is-focusable horizontal onClick={partial(onFeatureClick, feature)}>
                                {defaultRender(pillProps)}
                            </Stack>
                            {isInProgress ? (
                                <Spinner size={SpinnerSize.small} styles={spinnerStyles} />
                            ) : (
                                onToggleRequire &&
                                canWrite && (
                                    <LuisTooltip styles={tooltipStyles} content={label}>
                                        <RequireIconButton
                                            customProps={{ isRequired }}
                                            role="button"
                                            ariaLabel={label}
                                            disabled={isDisabled || isRequiredToggleDisabled}
                                            styles={toggleRequiredButtonStyles}
                                            iconProps={{ iconName: 'Asterisk' }}
                                            onClick={e => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onToggleRequire(feature, !feature.isRequired);
                                            }}
                                        />
                                    </LuisTooltip>
                                )
                            )}
                        </Stack>
                    )
                };
            }),
        [existingFeatures, disabledFeatureIds, onToggleRequire]
    );

    /**
     * Construct an add phrase list item as an
     * option when the query doesn't render any items.
     */
    const onInputPillMenuNoContent = (query: string): IContextualMenuItem[] => [
        {
            key: getId('addPhraseListItem'),
            text: `${query ? `'${query}'` : ''} ${localize(messages.createPhraseListItem)}`,
            iconProps: { iconName: !query && 'Add' },
            onClick: () => {
                onCreateNewPhraseList(query);
                setShowInputPill(false);
            }
        }
    ];

    /**
     * Exclude the features that were already added
     * from the list of features to show in the contextual menu.
     */
    const filteredFeatures = differenceWith(availableFeatures, existingFeatures, (a, b) => a.id === b.id);

    /**
     * Creates contextual menu items based on the features passed.
     */
    const getContextualMenuItems = (): IContextualMenuItem[] => {
        const entities = filteredFeatures.filter(d => d.typeId === 'entity');
        const phraseLists = filteredFeatures.filter(d => d.typeId === 'feature');

        const menuItems: IContextualMenuItem[] = [];

        menuItems.push(
            ...entities.map(e =>
                getContextualMenuItem(
                    e,
                    () => {
                        setShowInputPill(false);
                        onAddFeature(e);
                    },
                    disabledFeatureIds
                )
            )
        );

        menuItems.push(
            ...phraseLists.map(p =>
                getContextualMenuItem(
                    p,
                    () => {
                        setShowInputPill(false);
                        onAddFeature(p);
                    },
                    disabledFeatureIds
                )
            )
        );

        return menuItems.sort((a, b) => (a.text.toLowerCase() > b.text.toLowerCase() ? 1 : -1));
    };

    return (
        <PillContainer
            allowAddPills
            pills={featurePills}
            addPillButtonRole="listitem"
            showInputPill={showInputPill}
            onAddNewPill={() => undefined}
            isLuisToolTipHidden={canWrite}
            isAddPillButtonDisabled={!canWrite}
            isDeletePillButtonDisabled={!canDeleteData}
            onRemovePill={() => setShowInputPill(false)}
            onAddIconClick={() => setShowInputPill(true)}
            inputPillMenuItems={getContextualMenuItems()}
            inputPillMenuNoContent={onInputPillMenuNoContent}
            addPillButtonAriaLabel={localize(messages.addFeature)}
            onRenderAddPillButtonContent={() => (
                <LuisText styles={addLabelStyles} variant="small">
                    {localize(messages.addFeature)}
                </LuisText>
            )}
            inputPillPlaceholder={localize(messages.inputPlaceholder)}
            disabledButtonLuisToolTipMessage={localize(globalMessages.cannotPerformAction, { displayedUserRole })}
        />
    );
};
